/**
 * Reading a file name the way a person does: a name, and a kind.
 *
 * Project documents are uploaded with names like "2025 - RBI25 - Legal Aid T&C - NL.pdf". Printing
 * that whole string as one line gives every document the same shape and puts the part that
 * distinguishes them — the beginning — at the mercy of the truncation. Splitting the extension off
 * lets the name wrap and the kind sit under it as a label.
 */

export type FileNameParts = {
  /** The name without its extension. Never empty for a non-empty input. */
  base: string;
  /** Lowercase extension without the dot, or `""` when there is none. */
  extension: string;
};

export function splitFileName(name: string): FileNameParts {
  const trimmed = name.trim();
  const lastDot = trimmed.lastIndexOf(".");

  // A leading dot is a hidden file, not an extension, and a trailing dot has nothing after it.
  if (lastDot <= 0 || lastDot === trimmed.length - 1) {
    return { base: trimmed, extension: "" };
  }

  const extension = trimmed.slice(lastDot + 1);

  // There is no list of real extensions to check against, so this is a shape test: two to five
  // characters, letters and digits, and at least one letter. Without that last rule "Handleiding
  // v1.2" loses its version number to an extension called "2".
  if (!/^(?=[a-z0-9]{2,5}$)[a-z0-9]*[a-z][a-z0-9]*$/i.test(extension)) {
    return { base: trimmed, extension: "" };
  }

  return { base: trimmed.slice(0, lastDot), extension: extension.toLowerCase() };
}

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "svg", "bmp"];

/** Whether this document is something you look at rather than read. */
export function isImageFile(extension: string): boolean {
  return IMAGE_EXTENSIONS.includes(extension.toLowerCase());
}
